import React, { useState, useEffect } from 'react';
import { formsAPI } from '../lib/api';
import { formatDate, downloadFile } from '../lib/utils';
import { 
  FileText, 
  Search, 
  Link2, 
  Download, 
  Star, 
  StarOff,
  ExternalLink,
  Edit,
  Trash2,
  Check,
  Loader2,
  AlertCircle,
  Filter,
  ChevronDown,
  FileCheck,
  Building,
  Scale,
  DollarSign,
  Calendar,
  Tag,
  MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';

const JURISDICTIONS = [
  { value: 'all', label: 'All Jurisdictions' },
  { value: 'ontario', label: 'Ontario' },
  { value: 'federal', label: 'Federal' },
  { value: 'british_columbia', label: 'British Columbia' },
  { value: 'alberta', label: 'Alberta' },
];

const COURT_AGENCIES = [
  { value: 'all', label: 'All Courts/Agencies' },
  { value: 'superior_court', label: 'Superior Court of Justice' },
  { value: 'ontario_court', label: 'Ontario Court of Justice' },
  { value: 'ltb', label: 'Landlord and Tenant Board' },
  { value: 'wsib', label: 'WSIB' },
  { value: 'hrto', label: 'Human Rights Tribunal' },
  { value: 'family_court', label: 'Family Court' },
  { value: 'small_claims', label: 'Small Claims Court' },
];

const PRACTICE_AREAS = [
  { value: 'all', label: 'All Practice Areas' },
  { value: 'civil', label: 'Civil Litigation' },
  { value: 'family', label: 'Family Law' },
  { value: 'criminal', label: 'Criminal Law' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'landlord_tenant', label: 'Landlord & Tenant' },
  { value: 'employment', label: 'Employment' },
  { value: 'immigration', label: 'Immigration' },
  { value: 'corporate', label: 'Corporate/Business' },
];

export const Forms = () => {
  // URL Extractor State
  const [scanUrl, setScanUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [extractedForms, setExtractedForms] = useState([]);
  const [selectedExtracted, setSelectedExtracted] = useState([]);
  const [sourceWebsite, setSourceWebsite] = useState('');
  const [scannedUrl, setScannedUrl] = useState(''); // Store the actual URL that was scanned
  const [importing, setImporting] = useState(false);
  
  // Form Library State
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    jurisdiction: 'all',
    court_agency: 'all',
    practice_area: 'all',
    is_fillable: null,
    is_favorited: null,
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Edit Dialog State
  const [editingForm, setEditingForm] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    fetchForms();
  }, [searchTerm, filters, sortBy, sortOrder]);

  const fetchForms = async () => {
    try {
      const params = {
        search: searchTerm || undefined,
        jurisdiction: filters.jurisdiction !== 'all' ? filters.jurisdiction : undefined,
        court_agency: filters.court_agency !== 'all' ? filters.court_agency : undefined,
        practice_area: filters.practice_area !== 'all' ? filters.practice_area : undefined,
        is_fillable: filters.is_fillable,
        is_favorited: filters.is_favorited,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      
      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = await formsAPI.getAll(params);
      setForms(response.data.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleScanUrl = async () => {
    if (!scanUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    
    const urlToScan = scanUrl.trim();
    setScanning(true);
    setExtractedForms([]);
    setSelectedExtracted([]);
    setScannedUrl(urlToScan); // Store the URL we're scanning
    
    try {
      console.log('Scanning URL:', urlToScan); // Debug log
      const response = await formsAPI.scanUrl(urlToScan);
      const data = response.data;
      console.log('Scan response:', data); // Debug log
      setExtractedForms(data.forms_found || []);
      setSourceWebsite(data.source_website || '');
      
      if (data.forms_found?.length === 0) {
        toast.info('No forms found on this page');
      } else {
        toast.success(`Found ${data.total_count} forms from ${urlToScan}`);
      }
    } catch (error) {
      console.error('Failed to scan URL:', error);
      toast.error('Failed to scan URL. Please check the URL and try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleImportSelected = async () => {
    if (selectedExtracted.length === 0) {
      toast.error('Please select at least one form to import');
      return;
    }
    
    setImporting(true);
    
    try {
      const formsToImport = extractedForms
        .filter((_, index) => selectedExtracted.includes(index))
        .map(form => ({
          title: form.title,
          form_number: form.form_number,
          source_url: form.url,
          source_website: sourceWebsite,
          file_type: form.file_type,
          jurisdiction: 'ontario', // Default
        }));
      
      const response = await formsAPI.importForms(formsToImport);
      toast.success(`Successfully imported ${response.data.imported} forms`);
      
      // Reset extractor
      setExtractedForms([]);
      setSelectedExtracted([]);
      setScanUrl('');
      
      // Refresh library
      fetchForms();
    } catch (error) {
      console.error('Failed to import forms:', error);
      toast.error('Failed to import forms');
    } finally {
      setImporting(false);
    }
  };

  const toggleExtractedSelection = (index) => {
    setSelectedExtracted(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const selectAllExtracted = () => {
    if (selectedExtracted.length === extractedForms.length) {
      setSelectedExtracted([]);
    } else {
      setSelectedExtracted(extractedForms.map((_, i) => i));
    }
  };

  const handleToggleFavorite = async (formId) => {
    try {
      const response = await formsAPI.toggleFavorite(formId);
      setForms(prev => prev.map(f => 
        f.form_id === formId ? { ...f, is_favorited: response.data.is_favorited } : f
      ));
    } catch (error) {
      toast.error('Failed to update favorite status');
    }
  };

  const handleDownload = async (form) => {
    try {
      const response = await formsAPI.download(form.form_id);
      
      if (response.data.redirect_url) {
        window.open(response.data.redirect_url, '_blank');
      } else {
        const filename = `${form.title}.${form.file_type || 'pdf'}`;
        const success = downloadFile(response.data, filename, 'application/pdf');
        if (!success) {
          toast.error('Failed to download - file may be empty');
        }
      }
    } catch (error) {
      toast.error('Failed to download form');
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) return;
    
    try {
      await formsAPI.delete(formId);
      toast.success('Form deleted');
      fetchForms();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const handleEditForm = async () => {
    if (!editingForm) return;
    
    try {
      await formsAPI.update(editingForm.form_id, editingForm);
      toast.success('Form updated');
      setEditDialogOpen(false);
      setEditingForm(null);
      fetchForms();
    } catch (error) {
      toast.error('Failed to update form');
    }
  };

  const getFileTypeBadge = (fileType) => {
    const colors = {
      pdf: 'bg-red-100 text-red-800',
      doc: 'bg-blue-100 text-blue-800',
      docx: 'bg-blue-100 text-blue-800',
      html: 'bg-purple-100 text-purple-800',
    };
    return colors[fileType] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Legal Forms</h1>
          <p className="text-slate-500 mt-1">Import, organize, and manage legal forms for your practice</p>
        </div>
        <Badge variant="outline" className="text-sm">
          {forms.length} forms in library
        </Badge>
      </div>

      {/* URL Extractor Section */}
      <Card data-testid="forms-extractor-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Import Forms
          </CardTitle>
          <CardDescription>
            Paste a URL from Ontario Court Forms, LTB, or other legal form repositories to scan and import forms
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Input
              placeholder="https://ontariocourtforms.on.ca/en/civil-forms/"
              value={scanUrl}
              onChange={(e) => setScanUrl(e.target.value)}
              className="flex-1"
              data-testid="scan-url-input"
            />
            <Button 
              onClick={handleScanUrl} 
              disabled={scanning}
              data-testid="scan-url-btn"
            >
              {scanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Scan & Extract Forms
                </>
              )}
            </Button>
          </div>

          {/* Extracted Forms Preview */}
          {extractedForms.length > 0 && (
            <div className="border rounded-lg bg-slate-50 overflow-hidden">
              <div className="p-4 border-b bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileCheck className="h-5 w-5 text-green-600" />
                    <span className="font-medium">
                      Found {extractedForms.length} forms
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllExtracted}>
                      {selectedExtracted.length === extractedForms.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button 
                      onClick={handleImportSelected} 
                      disabled={selectedExtracted.length === 0 || importing}
                      data-testid="import-selected-btn"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Import Selected ({selectedExtracted.length})
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              {/* Show the scanned URL prominently */}
              <div className="px-4 py-2 bg-blue-50 border-b text-sm flex items-center gap-2">
                <Link2 className="h-4 w-4 text-blue-600" />
                <span className="text-blue-700 font-medium">Scanned:</span>
                <a 
                  href={scannedUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-blue-600 hover:underline truncate flex-1"
                  title={scannedUrl}
                >
                  {scannedUrl}
                </a>
                <ExternalLink className="h-3 w-3 text-blue-400" />
              </div>
              
              <div className="max-h-96 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 border-b sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600 w-10">
                        <Checkbox 
                          checked={selectedExtracted.length === extractedForms.length && extractedForms.length > 0}
                          onCheckedChange={selectAllExtracted}
                        />
                      </th>
                      <th className="text-left p-3 font-medium text-slate-600">Form Number</th>
                      <th className="text-left p-3 font-medium text-slate-600">Form Title</th>
                      <th className="text-left p-3 font-medium text-slate-600">Format</th>
                      <th className="text-left p-3 font-medium text-slate-600">Source URL</th>
                      <th className="text-left p-3 font-medium text-slate-600 w-20">Preview</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-white">
                    {extractedForms.map((form, index) => (
                      <tr 
                        key={index}
                        className={`hover:bg-blue-50 cursor-pointer transition-colors ${
                          selectedExtracted.includes(index) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => toggleExtractedSelection(index)}
                      >
                        {/* Checkbox */}
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedExtracted.includes(index)}
                            onCheckedChange={() => toggleExtractedSelection(index)}
                          />
                        </td>
                        
                        {/* Form Number */}
                        <td className="p-3">
                          {form.form_number ? (
                            <span className="font-mono text-slate-700 font-medium">
                              {form.form_number}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        
                        {/* Form Title */}
                        <td className="p-3 max-w-sm">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium text-slate-900 truncate" title={form.title}>
                              {form.title}
                            </span>
                            {form.description && (
                              <span className="text-xs text-slate-500 truncate" title={form.description}>
                                {form.description}
                              </span>
                            )}
                          </div>
                        </td>
                        
                        {/* Format */}
                        <td className="p-3">
                          <Badge className={`text-xs ${getFileTypeBadge(form.file_type)}`}>
                            {form.file_type?.toUpperCase() || 'HTML'}
                          </Badge>
                        </td>
                        
                        {/* Source URL */}
                        <td className="p-3 max-w-xs">
                          <span className="text-xs text-slate-500 truncate block" title={form.url}>
                            {form.url?.length > 50 ? `${form.url.substring(0, 50)}...` : form.url}
                          </span>
                        </td>
                        
                        {/* Preview Link */}
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-blue-600 hover:text-blue-700"
                            onClick={() => window.open(form.url, '_blank')}
                            title="Open in new tab"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="p-3 bg-slate-100 border-t text-xs text-slate-500">
                <div className="flex items-center justify-between">
                  <span>{selectedExtracted.length} of {extractedForms.length} forms selected</span>
                  <span>Source: {sourceWebsite}</span>
                </div>
                <div className="mt-1 text-slate-400 truncate" title={scannedUrl}>
                  Scanned URL: {scannedUrl}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Library Section */}
      <Card data-testid="forms-library-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Form Library
          </CardTitle>
          <CardDescription>
            Search and filter your imported legal forms
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search & Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search forms by title, number, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-forms-input"
              />
            </div>
            
            <Select value={filters.jurisdiction} onValueChange={(v) => setFilters(p => ({...p, jurisdiction: v}))}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Jurisdiction" />
              </SelectTrigger>
              <SelectContent>
                {JURISDICTIONS.map(j => (
                  <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.court_agency} onValueChange={(v) => setFilters(p => ({...p, court_agency: v}))}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Court/Agency" />
              </SelectTrigger>
              <SelectContent>
                {COURT_AGENCIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.practice_area} onValueChange={(v) => setFilters(p => ({...p, practice_area: v}))}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Practice Area" />
              </SelectTrigger>
              <SelectContent>
                {PRACTICE_AREAS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Recently Added</SelectItem>
                <SelectItem value="title">Alphabetical</SelectItem>
                <SelectItem value="use_count">Most Used</SelectItem>
                <SelectItem value="filing_fee">Filing Fee</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* View Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button
                variant={filters.is_favorited === true ? "default" : "outline"}
                size="sm"
                onClick={() => setFilters(p => ({...p, is_favorited: p.is_favorited === true ? null : true}))}
              >
                <Star className="h-3 w-3 mr-1" />
                Favorites
              </Button>
              <Button
                variant={filters.is_fillable === true ? "default" : "outline"}
                size="sm"
                onClick={() => setFilters(p => ({...p, is_fillable: p.is_fillable === true ? null : true}))}
              >
                <FileCheck className="h-3 w-3 mr-1" />
                Fillable Only
              </Button>
            </div>
            <span className="text-sm text-slate-500">{forms.length} forms</span>
          </div>

          {/* Forms Table */}
          {forms.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No forms in your library yet</p>
              <p className="text-sm mt-1">Use the Import Forms section above to add forms from legal form websites</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600 w-8"></th>
                      <th className="text-left p-3 font-medium text-slate-600">Form Number</th>
                      <th className="text-left p-3 font-medium text-slate-600">Form Title</th>
                      <th className="text-left p-3 font-medium text-slate-600">Version Date</th>
                      <th className="text-left p-3 font-medium text-slate-600">Effective Date</th>
                      <th className="text-left p-3 font-medium text-slate-600">Court/Agency</th>
                      <th className="text-left p-3 font-medium text-slate-600">Filing Fee</th>
                      <th className="text-left p-3 font-medium text-slate-600">Available Formats</th>
                      <th className="text-left p-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {forms.map((form) => (
                      <tr 
                        key={form.form_id} 
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`form-row-${form.form_id}`}
                      >
                        {/* Favorite */}
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleToggleFavorite(form.form_id)}
                          >
                            {form.is_favorited ? (
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            ) : (
                              <StarOff className="h-4 w-4 text-slate-300 hover:text-slate-400" />
                            )}
                          </Button>
                        </td>
                        
                        {/* Form Number */}
                        <td className="p-3">
                          <span className="font-mono text-slate-700 font-medium">
                            {form.form_number || '—'}
                          </span>
                        </td>
                        
                        {/* Form Title */}
                        <td className="p-3 max-w-xs">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-slate-900 truncate" title={form.title}>
                              {form.title}
                            </span>
                            <div className="flex items-center gap-1.5">
                              {form.is_fillable && (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 h-5">
                                  Fillable
                                </Badge>
                              )}
                              {form.jurisdiction && (
                                <Badge variant="outline" className="text-xs capitalize h-5">
                                  {form.jurisdiction}
                                </Badge>
                              )}
                              {form.practice_area && (
                                <Badge variant="outline" className="text-xs capitalize h-5 bg-blue-50 text-blue-700 border-blue-200">
                                  {form.practice_area.replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        {/* Version Date */}
                        <td className="p-3 text-slate-600">
                          {form.version_date ? formatDate(form.version_date) : form.last_updated ? formatDate(form.last_updated) : '—'}
                        </td>
                        
                        {/* Effective Date */}
                        <td className="p-3 text-slate-600">
                          {form.effective_date ? formatDate(form.effective_date) : '—'}
                        </td>
                        
                        {/* Court/Agency */}
                        <td className="p-3 text-slate-600">
                          <div className="flex flex-col">
                            <span className="capitalize">{form.court_agency?.replace(/_/g, ' ') || '—'}</span>
                            {form.source_website && (
                              <span className="text-xs text-slate-400 truncate max-w-[150px]" title={form.source_website}>
                                {form.source_website}
                              </span>
                            )}
                          </div>
                        </td>
                        
                        {/* Filing Fee */}
                        <td className="p-3">
                          {form.filing_fee !== null && form.filing_fee !== undefined ? (
                            <div className="flex flex-col">
                              <span className={form.filing_fee === 0 ? 'text-green-600 font-medium' : 'text-slate-700'}>
                                {form.filing_fee === 0 ? 'Free' : `$${form.filing_fee.toFixed(2)}`}
                              </span>
                              {form.fee_notes && (
                                <span className="text-xs text-slate-400 truncate max-w-[100px]" title={form.fee_notes}>
                                  {form.fee_notes}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        
                        {/* Available Formats */}
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              {/* PDF/HTML Badge */}
                              <Badge className={`text-xs ${getFileTypeBadge(form.file_type)} cursor-pointer`} onClick={() => handleDownload(form)}>
                                {form.file_type?.toUpperCase()}
                              </Badge>
                              {/* Word form if available */}
                              {form.word_form_url && (
                                <Badge 
                                  className="text-xs bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                                  onClick={() => window.open(form.word_form_url, '_blank')}
                                >
                                  DOCX
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {form.file_size && (
                                <span className="text-xs text-slate-400">
                                  {form.file_size > 1024 * 1024 
                                    ? `${(form.file_size / (1024 * 1024)).toFixed(1)} MB`
                                    : `${Math.round(form.file_size / 1024)} KB`
                                  }
                                </span>
                              )}
                              {form.instructions_url && (
                                <a 
                                  href={form.instructions_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  Instructions
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        {/* Actions */}
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleDownload(form)}
                              title="Download Form"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-slate-600 hover:text-slate-700"
                              onClick={() => window.open(form.source_url, '_blank')}
                              title="View Original Source"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => { setEditingForm(form); setEditDialogOpen(true); }}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Metadata
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(form.form_id)}>
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Form Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Form Metadata</DialogTitle>
            <DialogDescription>
              Update the metadata for this form. This helps with search and organization.
            </DialogDescription>
          </DialogHeader>
          
          {editingForm && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Form Title</Label>
                  <Input
                    value={editingForm.title}
                    onChange={(e) => setEditingForm({...editingForm, title: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Form Number</Label>
                  <Input
                    value={editingForm.form_number || ''}
                    onChange={(e) => setEditingForm({...editingForm, form_number: e.target.value})}
                    placeholder="e.g., Form 4, L1"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Jurisdiction</Label>
                  <Select
                    value={editingForm.jurisdiction}
                    onValueChange={(v) => setEditingForm({...editingForm, jurisdiction: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {JURISDICTIONS.filter(j => j.value !== 'all').map(j => (
                        <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Version Date</Label>
                  <Input
                    type="date"
                    value={editingForm.version_date ? new Date(editingForm.version_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditingForm({...editingForm, version_date: e.target.value || null})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={editingForm.effective_date ? new Date(editingForm.effective_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditingForm({...editingForm, effective_date: e.target.value || null})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Court/Agency</Label>
                  <Select
                    value={editingForm.court_agency || ''}
                    onValueChange={(v) => setEditingForm({...editingForm, court_agency: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {COURT_AGENCIES.filter(c => c.value !== 'all').map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Practice Area</Label>
                  <Select
                    value={editingForm.practice_area || ''}
                    onValueChange={(v) => setEditingForm({...editingForm, practice_area: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PRACTICE_AREAS.filter(p => p.value !== 'all').map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Filing Fee</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingForm.filing_fee || ''}
                    onChange={(e) => setEditingForm({...editingForm, filing_fee: e.target.value ? parseFloat(e.target.value) : null})}
                    placeholder="0.00"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Filing Method</Label>
                  <Select
                    value={editingForm.filing_method || ''}
                    onValueChange={(v) => setEditingForm({...editingForm, filing_method: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="in_person">In Person</SelectItem>
                      <SelectItem value="mail">Mail</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label>Where to File</Label>
                  <Input
                    value={editingForm.where_to_file || ''}
                    onChange={(e) => setEditingForm({...editingForm, where_to_file: e.target.value})}
                    placeholder="e.g., Superior Court of Justice, 393 University Ave, Toronto"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>MS Word Form URL</Label>
                  <Input
                    value={editingForm.word_form_url || ''}
                    onChange={(e) => setEditingForm({...editingForm, word_form_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Instructions URL</Label>
                  <Input
                    value={editingForm.instructions_url || ''}
                    onChange={(e) => setEditingForm({...editingForm, instructions_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editingForm.description || ''}
                    onChange={(e) => setEditingForm({...editingForm, description: e.target.value})}
                    placeholder="Brief description of the form's purpose..."
                    rows={2}
                  />
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label>Fee Notes</Label>
                  <Textarea
                    value={editingForm.fee_notes || ''}
                    onChange={(e) => setEditingForm({...editingForm, fee_notes: e.target.value})}
                    placeholder="Fee waivers, exemptions, etc."
                    rows={2}
                  />
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    value={(editingForm.tags || []).join(', ')}
                    onChange={(e) => setEditingForm({
                      ...editingForm, 
                      tags: e.target.value.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
                    })}
                    placeholder="motion, urgent, court"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEditForm}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Forms;
